import DatePicker from 'components/Common/Calendar/DatePicker'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import InputText from 'LuxKit/Typography/InputText'
import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import useAirportTransferDateTimePickerControls from '../hooks/useAirportTransferDateTimePickerControls'

const DatePickerInput = styled(TextInputOnly)`
  justify-content: start;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  height: 100%;
  border-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.round};

  :focus-within {
    border-color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

const DropdownBody = styled(VerticalSpacer)`
  width: 390px;
  padding-top: ${rem(12)};
  padding-bottom: ${rem(16)};
`

const TimeButton = styled(TextButton)`
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  display: grid;
  grid-template-columns: auto 1fr auto;
  text-align: start;
`

interface Props {
  label?: string
  timeInputLabel?: string
  selectedDate?: moment.Moment
  selectedTime?: string
  isOpen?: boolean
  disabled?: boolean
  required?: boolean
  pickUpDate?: moment.Moment
  minDate?: moment.Moment
  minTime?: string
  maxDate?: moment.Moment
  maxTime?: string
  onDatePickerOpen?: () => void
  onDatePickerClose?: () => void
  onChange?: ({ date, time }: { date?: moment.Moment, time?: string }) => void
}

function CarsAirportTransferDateTimePicker(props: Props) {
  const {
    label,
    timeInputLabel,
    onChange,
    selectedDate,
    selectedTime,
    isOpen = false,
    onDatePickerClose,
    onDatePickerOpen,
    pickUpDate,
    disabled,
    required = true,
    minDate,
    minTime,
    maxDate,
    maxTime,
  } = props

  const [localDate, setLocalDate] = useState(selectedDate)
  const [localTime, setLocalTime] = useState(selectedTime || '09:00')

  useEffect(() => {
    setLocalDate(selectedDate)
    setLocalTime(selectedTime || '09:00')
  }, [isOpen, selectedDate, selectedTime])

  const {
    min,
    max,
    dateAndTimeLabel,
    timeLabel,
    timeOptions,
    timePickerBodyRef,
    isTimePickerOpen,
    onOpenTimePicker,
    onCloseTimePicker,
    dayCellRender,
    getAvailableTimeForDate,
  } = useAirportTransferDateTimePickerControls({ selectedDate: localDate, selectedTime: localTime, pickUpDate, minDate, minTime, maxDate, maxTime })

  const datePickerInputRef = useRef(null)
  const timePickerButtonRef = useRef(null)

  const dateSelectedDay = useMemo(() => localDate?.toDate(), [localDate])

  useEffect(() => {
    if (!localDate) return

    setLocalTime(currentTime => getAvailableTimeForDate(localDate, currentTime))
  }, [getAvailableTimeForDate, localDate])

  const onDateChange = useCallback((day: Date) => {
    setLocalDate(moment(day))
    onOpenTimePicker()
  }, [onOpenTimePicker])

  const closeTimePicker = useCallback(() => {
    if (localDate) onChange?.({ date: localDate, time: localTime })
    onCloseTimePicker()
  }, [localDate, localTime, onCloseTimePicker, onChange])

  const onTimeChange = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.dataset.value
    if (!value) return

    setLocalTime(value)
    if (localDate) onChange?.({ date: localDate, time: value })
    onCloseTimePicker()
  }, [localDate, onChange, onCloseTimePicker])

  return (
    <>
      <DatePickerInput
        ref={datePickerInputRef}
        onFocus={onDatePickerOpen}
        placeholder={label}
        value={dateAndTimeLabel}
        noValidationMessage
        noValidationSpacing
        required={required}
        disabled={disabled}
      />
      <DropdownSheet
        size="content"
        open={isOpen}
        anchorRef={datePickerInputRef}
        triggerRef={datePickerInputRef}
        onClose={onDatePickerClose}
        portaled
        placement="bottom-start"
        noPaddingOnBody
      >
        <DropdownBody gap={0}>
          <Sheet padding="20 20">
            <DatePicker
              min={min}
              max={max}
              value={dateSelectedDay}
              focusMonth={pickUpDate?.toDate()}
              onDayClick={onDateChange}
              dayRender={dayCellRender}
              fixedHeight={false}
            />
          </Sheet>
          <Divider kind="primary" />
          <Sheet padding="12 20">
            <Group direction="vertical" gap={4}>
              <InputText variant="label">{timeInputLabel}</InputText>
              <TimeButton kind="primary" variant="dark" startIcon={<LineClockIcon />} endIcon={<LineAngleDownIcon />} onClick={onOpenTimePicker} ref={timePickerButtonRef}>{timeLabel}</TimeButton>
              <DropdownList
                anchorRef={timePickerButtonRef}
                bodyRef={timePickerBodyRef}
                open={isTimePickerOpen}
                size="S"
                placement="top"
                triggerRef={timePickerButtonRef}
                onClose={closeTimePicker}
              >
                {timeOptions.map(time => <ClickableListItem
                  selected={time.value === localTime}
                  key={time.value}
                  title={time.label}
                  data-value={time.value}
                  size="medium"
                  onClick={onTimeChange}
                  disabled={time.disabled}
                />)}
              </DropdownList>
            </Group>
          </Sheet>
        </DropdownBody>
      </DropdownSheet>
    </>
  )
}

export default CarsAirportTransferDateTimePicker
